/**
 * lib/device/deviceId.ts
 *
 * P0-1 · Fase 8B — Identificador estable de dispositivo/instalación.
 *
 * - UUID v4 (crypto.randomUUID), creado UNA sola vez por navegador/instalación.
 * - Persistido en IndexedDB (app_meta), no solo en localStorage.
 * - No cambia al cerrar sesión ni al cambiar de tienda/empresa.
 * - No contiene datos personales ni fingerprinting de hardware.
 * - No se regenera en upgrades normales de Dexie (la tabla se preserva).
 *
 * Concurrencia: la creación get-or-create ocurre dentro de UNA transacción rw
 * sobre app_meta. IndexedDB serializa transacciones rw con scope solapado entre
 * conexiones (pestañas), por lo que dos tabs convergen al mismo device_id; el
 * ConstraintError por 'add' duplicado se maneja releyendo.
 */

import { db } from '@lib/dexie/schema'

const DEVICE_ID_KEY = 'device_id'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

let cached: string | null = null

export function isValidDeviceId(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

/**
 * Devuelve el device_id estable, creándolo de forma atómica si no existe.
 * Cachea en memoria tras la primera resolución.
 */
export async function getOrCreateDeviceId(): Promise<string> {
  if (cached) return cached

  const resolved = await db.transaction('rw', db.app_meta, async () => {
    const existing = await db.app_meta.get(DEVICE_ID_KEY)
    if (existing && isValidDeviceId(existing.value)) {
      return existing.value
    }

    const id = crypto.randomUUID()
    try {
      await db.app_meta.add({ key: DEVICE_ID_KEY, value: id, updated_at: Date.now() })
      return id
    } catch {
      // Otra pestaña lo creó en paralelo → releer el valor ganador.
      const winner = await db.app_meta.get(DEVICE_ID_KEY)
      if (winner && isValidDeviceId(winner.value)) return winner.value
      throw new Error('No se pudo inicializar device_id')
    }
  })

  cached = resolved
  return resolved
}

/** Lee el device_id sin crearlo (null si aún no existe). Para lecturas rápidas. */
export async function peekDeviceId(): Promise<string | null> {
  if (cached) return cached
  const row = await db.app_meta.get(DEVICE_ID_KEY)
  return row && isValidDeviceId(row.value) ? row.value : null
}
